import * as THREE from 'three'
import { Heightfield } from '../terrain/heightfield'
import { ROCK, SNOW_TYPES, SNOW_TYPE_COUNT, Snow } from '../terrain/snow'
import { RAD, clamp01, lerp, smoothstep } from '../core/math'
import { Noise2D, mulberry32 } from '../core/rng'
import { createSnowMaterial } from './snowMaterial'

/**
 * Chunked, LOD'd terrain.
 *
 * Shading is smooth now, not faceted. The flat-shaded low-poly look was the
 * strongest stylisation in the scene — and it was structural, since per-face
 * colour requires duplicated vertices and per-face normals. Normals here are
 * taken analytically from the heightfield rather than averaged from triangles,
 * which is both cheaper and more accurate: the surface is a height function, so
 * its true normal is known everywhere and does not depend on how the mesh
 * happened to be tessellated. That also means normals no longer jump between
 * LOD levels.
 */

// Larger chunks at the finer grid: 1.8m cells would otherwise mean ~176 chunks
// and a draw call for each, doubled again by the shadow pass.
const CHUNK_CELLS = 48
const LOD_NEAR = 190
const LOD_MID = 430
const BUILD_BUDGET = 2

/**
 * Curvature scales, calibrated to this terrain's measured concavity: drainages
 * sit around 0.05 and the 90th percentile is ~0.12. Scaled wrong these flatten
 * into an on/off switch and grey out the mountain.
 */
const AO_SCALE = 8
const LIP_SCALE = 6

export type ColorMode = 'snow' | 'slope' | 'hazard'

/** Eight compass directions for the horizon scan, unit vectors, flat. */
const HORIZON_DIR = (() => {
  const out = new Float32Array(16)
  for (let d = 0; d < 8; d++) {
    const a = (d / 8) * Math.PI * 2
    out[d * 2] = Math.cos(a)
    out[d * 2 + 1] = Math.sin(a)
  }
  return out
})()
/** Sample distances, metres — near for tree wells, far for corridor walls. */
const HORIZON_DIST = [3.5, 8, 17, 33] as const

interface Chunk {
  ix: number
  iz: number
  cellsX: number
  cellsZ: number
  centre: THREE.Vector3
  radius: number
  mesh: THREE.Mesh
  lod: number
  wanted: number
  cache: Map<number, THREE.BufferGeometry>
}

export class TerrainMesh {
  readonly group = new THREE.Group()
  private readonly chunks: Chunk[] = []
  private readonly material: THREE.MeshStandardMaterial
  private readonly debugMaterial: THREE.MeshBasicMaterial

  /** Per-grid-vertex baked fields. */
  private readonly obstacles: import('../physics/obstacles').ObstacleField | null
  private colorSnow: Float32Array
  private colorSlope: Float32Array
  private colorHazard: Float32Array
  private normals: Float32Array
  /** (detail strength, roughness, rock) per vertex — read by the snow shader. */
  private surface: Float32Array

  private mode: ColorMode = 'snow'
  private readonly grain = new Noise2D(mulberry32(0x7a1e5c03))

  constructor(
    private readonly hf: Heightfield,
    detailFadeStart?: number,
    detailFadeEnd?: number,
    obstacles?: import('../physics/obstacles').ObstacleField,
  ) {
    this.obstacles = obstacles ?? null
    this.material = createSnowMaterial(detailFadeStart, detailFadeEnd)
    this.debugMaterial = new THREE.MeshBasicMaterial({ vertexColors: true })

    const n = hf.cols * hf.rows
    this.colorSnow = new Float32Array(n * 3)
    this.colorSlope = new Float32Array(n * 3)
    this.colorHazard = new Float32Array(n * 3)
    this.normals = new Float32Array(n * 3)
    this.surface = new Float32Array(n * 3)
    this.bake()

    const cellsX = hf.cols - 1
    const cellsZ = hf.rows - 1
    const nx = Math.ceil(cellsX / CHUNK_CELLS)
    const nz = Math.ceil(cellsZ / CHUNK_CELLS)

    for (let cz = 0; cz < nz; cz++) {
      for (let cx = 0; cx < nx; cx++) {
        const cw = Math.min(CHUNK_CELLS, cellsX - cx * CHUNK_CELLS)
        const cl = Math.min(CHUNK_CELLS, cellsZ - cz * CHUNK_CELLS)
        if (cw <= 0 || cl <= 0) continue

        const x0 = hf.xAt(cx * CHUNK_CELLS)
        const z0 = hf.zAt(cz * CHUNK_CELLS)
        const x1 = x0 + cw * hf.cell
        const z1 = z0 + cl * hf.cell

        let minY = Infinity
        let maxY = -Infinity
        for (let iz = 0; iz <= cl; iz += 4) {
          for (let ix = 0; ix <= cw; ix += 4) {
            const h = hf.heightAtIndex(cx * CHUNK_CELLS + ix, cz * CHUNK_CELLS + iz)
            if (h < minY) minY = h
            if (h > maxY) maxY = h
          }
        }

        const centre = new THREE.Vector3((x0 + x1) / 2, (minY + maxY) / 2, (z0 + z1) / 2)
        const radius = Math.hypot((x1 - x0) / 2, (maxY - minY) / 2, (z1 - z0) / 2) * 1.05

        const mesh = new THREE.Mesh(new THREE.BufferGeometry(), this.material)
        mesh.frustumCulled = true
        mesh.matrixAutoUpdate = false
        mesh.updateMatrix()
        mesh.castShadow = true
        mesh.receiveShadow = true
        this.group.add(mesh)

        const chunk: Chunk = {
          ix: cx, iz: cz, cellsX: cw, cellsZ: cl,
          centre, radius, mesh, lod: 0, wanted: 0, cache: new Map(),
        }
        this.chunks.push(chunk)
        this.applyLod(chunk, this.clampLod(chunk, 4))
      }
    }
  }

  get chunkCount(): number {
    return this.chunks.length
  }

  setColorMode(mode: ColorMode): void {
    if (mode === this.mode) return
    this.mode = mode
    // The debug views are flat data readouts; shading them would defeat them.
    const mat = mode === 'snow' ? this.material : this.debugMaterial
    for (const c of this.chunks) {
      for (const g of c.cache.values()) g.dispose()
      c.cache.clear()
      c.mesh.material = mat
      const lod = c.lod || 4
      c.lod = 0
      this.applyLod(c, lod)
    }
  }

  update(cameraPos: THREE.Vector3): void {
    let built = 0
    for (const c of this.chunks) {
      const d = cameraPos.distanceTo(c.centre) - c.radius
      const target = d < LOD_NEAR ? 1 : d < LOD_MID ? 2 : d < LOD_MID * 2.4 ? 4 : 8
      c.wanted = this.clampLod(c, target)
    }
    this.chunks.sort((a, b) => a.centre.distanceToSquared(cameraPos) - b.centre.distanceToSquared(cameraPos))
    for (const c of this.chunks) {
      if (c.lod !== c.wanted && built < BUILD_BUDGET) {
        this.applyLod(c, c.wanted)
        built++
      }
      if (c.wanted >= 4) {
        const fine = c.cache.get(1)
        if (fine && c.lod !== 1) {
          fine.dispose()
          c.cache.delete(1)
        }
      }
    }
  }

  private clampLod(c: Chunk, step: number): number {
    let s = step
    while (s > 1 && (c.cellsX % s !== 0 || c.cellsZ % s !== 0)) s >>= 1
    return s
  }

  private applyLod(c: Chunk, lod: number): void {
    if (c.lod === lod) return
    let geo = c.cache.get(lod)
    if (!geo) {
      geo = this.buildChunkGeometry(c, lod)
      c.cache.set(lod, geo)
    }
    c.mesh.geometry = geo
    c.lod = lod
  }

  // -- Geometry ------------------------------------------------------------

  private buildChunkGeometry(c: Chunk, step: number): THREE.BufferGeometry {
    const hf = this.hf
    const colors = this.mode === 'snow' ? this.colorSnow
      : this.mode === 'slope' ? this.colorSlope : this.colorHazard
    const baseIx = c.ix * CHUNK_CELLS
    const baseIz = c.iz * CHUNK_CELLS
    const qx = c.cellsX / step
    const qz = c.cellsZ / step

    const surfaceTris = qx * qz * 2
    const skirtTris = (qx + qz) * 2 * 2
    const vertCount = (surfaceTris + skirtTris) * 3

    const pos = new Float32Array(vertCount * 3)
    const nor = new Float32Array(vertCount * 3)
    const col = new Float32Array(vertCount * 3)
    const snw = new Float32Array(vertCount * 3)
    let v = 0

    const gIndex = (i: number, j: number): number => hf.index(
      Math.min(baseIx + i * step, hf.cols - 1),
      Math.min(baseIz + j * step, hf.rows - 1),
    )
    const gx = (i: number): number => hf.xAt(baseIx + i * step)
    const gz = (j: number): number => hf.zAt(baseIz + j * step)
    const gh = (i: number, j: number): number => hf.heightAtIndex(baseIx + i * step, baseIz + j * step)

    /** Emit one vertex, pulling its shading data from the baked grid. */
    const vert = (x: number, y: number, z: number, gi: number, darken: number): void => {
      const o = v * 3
      pos[o] = x; pos[o + 1] = y; pos[o + 2] = z
      nor[o] = this.normals[gi * 3]
      nor[o + 1] = this.normals[gi * 3 + 1]
      nor[o + 2] = this.normals[gi * 3 + 2]
      col[o] = colors[gi * 3] * darken
      col[o + 1] = colors[gi * 3 + 1] * darken
      col[o + 2] = colors[gi * 3 + 2] * darken
      snw[v * 3] = this.surface[gi * 3]
      snw[v * 3 + 1] = this.surface[gi * 3 + 1]
      snw[v * 3 + 2] = this.surface[gi * 3 + 2]
      v++
    }

    // Emits A, C, B — grid order winds clockwise from above and would give a
    // -Y face normal, backface-culling the entire mountain.
    const tri = (
      ax: number, ay: number, az: number, ai: number,
      bx: number, by: number, bz: number, bi: number,
      cx2: number, cy: number, cz2: number, ci: number,
      darken = 1,
    ): void => {
      vert(ax, ay, az, ai, darken)
      vert(cx2, cy, cz2, ci, darken)
      vert(bx, by, bz, bi, darken)
    }

    for (let j = 0; j < qz; j++) {
      for (let i = 0; i < qx; i++) {
        const x0 = gx(i), x1 = gx(i + 1)
        const z0 = gz(j), z1 = gz(j + 1)
        const h00 = gh(i, j), h10 = gh(i + 1, j)
        const h01 = gh(i, j + 1), h11 = gh(i + 1, j + 1)
        const i00 = gIndex(i, j), i10 = gIndex(i + 1, j)
        const i01 = gIndex(i, j + 1), i11 = gIndex(i + 1, j + 1)

        tri(x0, h00, z0, i00, x1, h10, z0, i10, x0, h01, z1, i01)
        tri(x1, h10, z0, i10, x1, h11, z1, i11, x0, h01, z1, i01)
      }
    }

    // Skirt: a curtain hiding the cracks where chunks meet at different LODs.
    const drop = hf.cell * step * 3 + 4
    const curtain = (
      ax: number, ay: number, az: number, ai: number,
      bx: number, by: number, bz: number, bi: number,
    ): void => {
      tri(ax, ay, az, ai, bx, by, bz, bi, ax, ay - drop, az, ai, 0.55)
      tri(bx, by, bz, bi, bx, by - drop, bz, bi, ax, ay - drop, az, ai, 0.55)
    }

    for (let i = 0; i < qx; i++) {
      curtain(gx(i + 1), gh(i + 1, 0), gz(0), gIndex(i + 1, 0), gx(i), gh(i, 0), gz(0), gIndex(i, 0))
      curtain(gx(i), gh(i, qz), gz(qz), gIndex(i, qz), gx(i + 1), gh(i + 1, qz), gz(qz), gIndex(i + 1, qz))
    }
    for (let j = 0; j < qz; j++) {
      curtain(gx(0), gh(0, j), gz(j), gIndex(0, j), gx(0), gh(0, j + 1), gz(j + 1), gIndex(0, j + 1))
      curtain(gx(qx), gh(qx, j + 1), gz(j + 1), gIndex(qx, j + 1), gx(qx), gh(qx, j), gz(j), gIndex(qx, j))
    }

    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3))
    geo.setAttribute('normal', new THREE.BufferAttribute(nor, 3))
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3))
    geo.setAttribute('aSnow', new THREE.BufferAttribute(snw, 3))
    geo.computeBoundingSphere()
    if (import.meta.env.DEV) assertFacesUp(geo, c)
    return geo
  }

  // -- Baking --------------------------------------------------------------

  private bake(): void {
    const hf = this.hf
    const n = { x: 0, y: 1, z: 0 }

    // Contact occlusion under every tree and rock, splatted into a grid once.
    // Nothing grounds an object like the ground going dark beneath it — the
    // trees previously met the snow at full brightness and read as placed on
    // top of the world rather than standing in it.
    const contact = new Float32Array(hf.cols * hf.rows).fill(1)
    if (this.obstacles) {
      for (const ob of this.obstacles.items) {
        const reach = ob.radius * 2.3 + 0.8
        const i0 = Math.max(0, Math.floor((ob.x - reach - hf.minX) / hf.cell))
        const i1 = Math.min(hf.cols - 1, Math.ceil((ob.x + reach - hf.minX) / hf.cell))
        const j0 = Math.max(0, Math.floor((ob.z - reach - hf.minZ) / hf.cell))
        const j1 = Math.min(hf.rows - 1, Math.ceil((ob.z + reach - hf.minZ) / hf.cell))
        for (let j = j0; j <= j1; j++) {
          for (let i = i0; i <= i1; i++) {
            const dx = hf.xAt(i) - ob.x
            const dz = hf.zAt(j) - ob.z
            const t = 1 - Math.hypot(dx, dz) / reach
            if (t <= 0) continue
            const idx = hf.index(i, j)
            contact[idx] = Math.min(contact[idx], 1 - 0.5 * t * t)
          }
        }
      }
    }

    for (let iz = 0; iz < hf.rows; iz++) {
      const z = hf.zAt(iz)
      for (let ix = 0; ix < hf.cols; ix++) {
        const x = hf.xAt(ix)
        const i = hf.index(ix, iz)
        const o = i * 3

        // Analytic normal — exact for a height function, and identical at every
        // LOD so the shading does not shift as chunks swap resolution.
        hf.sampleNormal(x, z, n)
        this.normals[o] = n.x
        this.normals[o + 1] = n.y
        this.normals[o + 2] = n.z

        const rock = hf.rock[i]
        const slope = hf.slopeAngleAt(x, z) * RAD
        const conc = hf.concavity[i]
        const wo = i * SNOW_TYPE_COUNT

        let r = 0, g = 0, b = 0
        let roughness = 0
        for (let k = 0; k < SNOW_TYPE_COUNT; k++) {
          const w = hf.snowWeights[wo + k]
          if (w <= 0) continue
          const cc = SNOW_TYPES[k].color
          r += cc[0] * w
          g += cc[1] * w
          b += cc[2] * w
          // Powder scatters almost perfectly; crust and ice take a polish.
          roughness += SNOW_ROUGHNESS[k] * w
        }

        if (rock > 0.01) {
          // Two tonal terms: curvature (ledges catch light, gullies do not) and
          // a broad stain, so a rock band is not one flat grey.
          const stain = this.grain.fbm(x * 0.021 + 55, z * 0.017 - 31, 3)
          const tone = (0.80 + 0.30 * clamp01((conc + 0.004) * 90)) * (1 + stain * 0.20)
          r = lerp(r, ROCK.color[0] * tone, rock)
          g = lerp(g, ROCK.color[1] * tone, rock)
          b = lerp(b, ROCK.color[2] * tone, rock)
          roughness = lerp(roughness, 0.95, rock)
        }

        const slab = hf.snowWeights[wo + Snow.WindSlab]
        const sluff = hf.sluffProne[i]

        // Broad tonal variation only. Fine ripple is the shader's job now — it
        // needs to move the shading normal, and colour alone shades flat.
        const drift = this.grain.fbm(x * 0.006, z * 0.005, 3) * 0.02
        const ao = 1 - clamp01(conc * AO_SCALE) * 0.22
        const lip = clamp01(-conc * LIP_SCALE) * smoothstep(30, 46, slope)
        const tint = 1 + drift

        // Horizon-sampled sky visibility. The probe lights the scene as if
        // every point saw the whole sky; a corridor floor between 27m walls
        // sees perhaps half of it, and that difference — gullies genuinely
        // darker than open faces — is most of what makes relief read as deep
        // rather than painted. Eight directions, four distances each, baked
        // once so it costs nothing at runtime.
        const y0 = hf.heightAtIndex(ix, iz)
        let occ = 0
        for (let d = 0; d < 8; d++) {
          const dx = HORIZON_DIR[d * 2]!
          const dz = HORIZON_DIR[d * 2 + 1]!
          let steep = 0
          for (const dist of HORIZON_DIST) {
            const dh = hf.sampleHeight(x + dx * dist, z + dz * dist) - y0
            if (dh > 0) {
              const tan = dh / dist
              const sin = tan / Math.sqrt(1 + tan * tan)
              if (sin > steep) steep = sin
            }
          }
          occ += steep
        }
        const sky = 1 - occ / 8
        const shade = (0.34 + 0.66 * Math.pow(clamp01(sky), 1.5)) * contact[i]!

        // Occluded snow goes blue, not grey: what still lights it is the sky.
        // Per-channel exponents bend the shadow colour without a second term.
        this.colorSnow[o] = clamp01((r * ao + lip * 0.10) * tint * Math.pow(shade, 1.18))
        this.colorSnow[o + 1] = clamp01((g * ao + lip * 0.11) * tint * Math.pow(shade, 1.06))
        this.colorSnow[o + 2] = clamp01((b * ao + lip * 0.13) * tint * Math.pow(shade, 0.86))

        // Detail relief where the wind or moving snow has worked the surface,
        // and never on bare rock.
        const detail =
          (0.5 + slab * 1.5 + sluff * 0.9) *
          clamp01((slope - 16) / 20) * (1 - rock) * 0.55
        this.surface[i * 3] = detail
        this.surface[i * 3 + 1] = clamp01(roughness)
        this.surface[i * 3 + 2] = rock

        const band = slopeBandColor(slope)
        this.colorSlope[o] = band[0] * ao
        this.colorSlope[o + 1] = band[1] * ao
        this.colorSlope[o + 2] = band[2] * ao

        const exp = hf.exposure[i]
        const trap = hf.trap[i]
        this.colorHazard[o] = clamp01(0.16 + exp * 0.9 + trap * 0.35)
        this.colorHazard[o + 1] = clamp01(0.16 + sluff * 0.55 + trap * 0.12)
        this.colorHazard[o + 2] = clamp01(0.2 + trap * 0.85)
      }
    }
  }

  dispose(): void {
    for (const c of this.chunks) {
      for (const g of c.cache.values()) g.dispose()
      c.cache.clear()
    }
    this.material.dispose()
    this.debugMaterial.dispose()
  }
}

/** Roughness per snow type, same order as SNOW_TYPES. */
const SNOW_ROUGHNESS = [0.93, 0.72, 0.52, 0.28]

/**
 * Winding guard. A heightfield surface must face up; wound backwards the whole
 * mountain is silently backface-culled while every triangle counter stays
 * healthy, because the triangles are submitted and simply never survive
 * culling.
 */
function assertFacesUp(geo: THREE.BufferGeometry, c: Chunk): void {
  const p = geo.getAttribute('position')
  const surfaceVerts = Math.floor(p.count * 0.8)
  let up = 0
  let checked = 0
  for (let i = 0; i + 2 < surfaceVerts; i += 33) {
    const ax = p.getX(i), az = p.getZ(i)
    const bx = p.getX(i + 1), bz = p.getZ(i + 1)
    const cx = p.getX(i + 2), cz = p.getZ(i + 2)
    // Only the Y component of the cross product matters for facing.
    const ux = bx - ax, uz = bz - az
    const vx = cx - ax, vz = cz - az
    if (uz * vx - ux * vz > 0) up++
    checked++
  }
  if (checked > 0 && up < checked * 0.5) {
    console.error(
      `[fall line] terrain chunk (${c.ix},${c.iz}) is wound inside out — ` +
        'the ground will be backface-culled and you will appear to ski on air.',
    )
  }
}

function slopeBandColor(deg: number): [number, number, number] {
  if (deg < 30) return [0.30, 0.72, 0.36]
  if (deg < 38) return [0.88, 0.82, 0.26]
  if (deg < 45) return [0.92, 0.53, 0.18]
  if (deg < 55) return [0.85, 0.22, 0.20]
  return [0.55, 0.13, 0.35]
}
